import { USE_RECORDING_V2 } from '@/components/constants/flags';
import { waitUntil } from '@/helpers/waitUntil';
import { UploadStatus } from '@/models/tauriRecordModel';
import { store } from '@/store';
import { isTauri } from '@/tauri/utils';
import chunkAudioRecorder from '@/utils/chunkAudioRecorder';
import { logger } from '@/utils/logger/logger';
import { reactQueryClient } from '@/utils/reactQueryClient';
import { getUploadProgress, waitForChunks } from '@heidi/capture/waitForChunks';
import { sanitizeError } from '@heidi/runtime/logger';
import {
  BackupUploadingStatus,
  type QuerySessionResponse,
} from '@heidi/session/contracts';
import {
  getTemporalTranscriptProgress,
  type TemporalTranscriptProgress,
  waitForTemporalTranscripts,
} from './chunkUploadV3';

export type WaitForChunksParams = {
  sessionID: string;
  queryKey: readonly [string, { sessionId: string }];
  disableChunkRecording?: boolean;
  isCreatingForContextOnlySession?: boolean;
  isRegenerate?: boolean;
  sessionFromCache?: QuerySessionResponse;
  isChunkRecordingEnabled: boolean;
  signal?: AbortSignal;
};

type UploadProgressState = {
  isComplete: boolean;
  progress: number;
};

const RECORDING_V2_PROGRESS_POLL_INTERVAL_MS = 500;

const clampProgress = (progress: number) => Math.max(0, Math.min(100, progress));

const setChunkUploadProgress = (
  queryKey: WaitForChunksParams['queryKey'],
  progress: number
) => {
  reactQueryClient.setQueryData<QuerySessionResponse>(queryKey, (oldData) => {
    if (!oldData) {
      return undefined;
    }

    return {
      ...oldData,
      chunkUploadProgress: clampProgress(progress),
    };
  });
};

const blendTranscriptProgress = (
  uploadProgress: number,
  { uploaded, persisted }: TemporalTranscriptProgress
) =>
  uploaded === 0
    ? uploadProgress
    : Math.floor((uploadProgress + (persisted / uploaded) * 100) / 2);

const getRecordingV2UploadProgressState = async (
  sessionID: string
): Promise<UploadProgressState> => getUploadProgress(sessionID);

const abortableSleep = (ms: number, signal?: AbortSignal, wake?: AbortSignal) =>
  new Promise<void>((resolve, reject) => {
    if (signal?.aborted) {
      const error = new Error('Upload progress polling was aborted');
      error.name = 'AbortError';
      reject(error);
      return;
    }
    if (wake?.aborted) {
      resolve();
      return;
    }
    const cleanup = () => {
      clearTimeout(timeout);
      signal?.removeEventListener('abort', onAbort);
      wake?.removeEventListener('abort', onWake);
    };
    const onWake = () => {
      cleanup();
      resolve();
    };
    const timeout = setTimeout(onWake, ms);
    const onAbort = () => {
      cleanup();
      const error = new Error('Upload progress polling was aborted');
      error.name = 'AbortError';
      reject(error);
    };
    signal?.addEventListener('abort', onAbort, { once: true });
    wake?.addEventListener('abort', onWake, { once: true });
  });

const waitForTemporalTranscriptsBeforeNote = async (
  sessionID: string,
  signal?: AbortSignal
) => {
  const { outcome, waitedMs, uploaded, persisted } = await waitForTemporalTranscripts(
    sessionID,
    signal
  );
  if (outcome === 'skipped') return;
  logger[outcome === 'complete' ? 'info' : 'warn'](
    '[waitForChunksToFinishUploading] v3 transcript wait',
    {
      event_type: 'V3_TRANSCRIPT_WAIT',
      session_id: sessionID,
      outcome,
      waited_ms: waitedMs,
      uploaded,
      persisted,
    }
  );
};

/**
 * Waits for audio chunks to finish uploading before proceeding with note generation.
 * On the temporal path it then waits, bounded and fail-open, for their transcripts.
 */
export async function waitForChunksToFinishUploading({
  sessionID,
  queryKey,
  disableChunkRecording,
  isCreatingForContextOnlySession,
  isRegenerate,
  sessionFromCache,
  isChunkRecordingEnabled,
  signal,
}: WaitForChunksParams): Promise<void> {
  let shownProgress = 0;

  if (isTauri()) {
    await waitUntil(
      () => {
        const chunksUploaded = store
          .getState()
          .tauriRecordModel.chunks.blobs.every(
            (chunk) =>
              chunk.status === UploadStatus.Completed ||
              chunk.status === UploadStatus.Failed
          );
        const backupUploaded = store.getState().tauriRecordModel.chunks.backup_uploaded;
        return Boolean(chunksUploaded || backupUploaded);
      },
      undefined,
      undefined,
      signal
    );
  } else if (
    isChunkRecordingEnabled &&
    !disableChunkRecording &&
    USE_RECORDING_V2() &&
    !isCreatingForContextOnlySession
  ) {
    const stopPolling = new AbortController();

    const progressPollingPromise = (async () => {
      while (!stopPolling.signal.aborted) {
        try {
          const uploadState = await getRecordingV2UploadProgressState(sessionID);
          const transcripts = getTemporalTranscriptProgress(sessionID);
          // Monotonic: a late upload can lower the transcribed share for a tick.
          shownProgress = Math.max(
            shownProgress,
            blendTranscriptProgress(uploadState.progress, transcripts)
          );
          setChunkUploadProgress(queryKey, shownProgress);

          if (uploadState.isComplete && transcripts.persisted === transcripts.uploaded) {
            return;
          }
        } catch (err) {
          logger.error(
            '[waitForChunksToFinishUploading] Progress polling error, stopping poll',
            {
              sessionID,
              error_type: err instanceof Error ? err.name : 'UnknownError',
              error: sanitizeError(err),
            }
          );
          return;
        }

        await abortableSleep(
          RECORDING_V2_PROGRESS_POLL_INTERVAL_MS,
          signal,
          stopPolling.signal
        );
      }
    })();

    try {
      await waitForChunks(sessionID, signal);
      await waitForTemporalTranscriptsBeforeNote(sessionID, signal);
    } finally {
      stopPolling.abort();
      await progressPollingPromise;
    }
  } else if (
    isChunkRecordingEnabled &&
    !disableChunkRecording &&
    !isCreatingForContextOnlySession
  ) {
    const shouldWaitForChunks =
      isRegenerate === false || sessionFromCache?.chunksUploadingFinished !== undefined;

    if (shouldWaitForChunks) {
      await waitUntil(
        () => {
          const sessionCacheUpdated =
            reactQueryClient.getQueryData<QuerySessionResponse>(queryKey);
          const backupUploaded =
            sessionCacheUpdated?.backupUploadingStatus === BackupUploadingStatus.SUCCESS;

          const areChunksUploaded =
            chunkAudioRecorder.allChunksUploaded() &&
            sessionCacheUpdated?.chunksUploadingFinished;
          const isBackupUsed =
            chunkAudioRecorder.chunkHasIssues() &&
            sessionCacheUpdated?.backupUploadingStatus === BackupUploadingStatus.SUCCESS;

          return Boolean(areChunksUploaded || isBackupUsed || backupUploaded);
        },
        undefined,
        undefined,
        signal
      );
    }
  }

  if (
    isChunkRecordingEnabled &&
    !disableChunkRecording &&
    USE_RECORDING_V2() &&
    !isCreatingForContextOnlySession
  ) {
    try {
      const uploadState = await getRecordingV2UploadProgressState(sessionID);
      setChunkUploadProgress(queryKey, Math.max(shownProgress, uploadState.progress));
    } catch (err) {
      // A store that cannot answer must not fail note generation: the wait is
      // already over, so the last polled progress stands.
      logger.error(
        '[waitForChunksToFinishUploading] Final progress read failed, keeping last progress',
        {
          sessionID,
          error_type: err instanceof Error ? err.name : 'UnknownError',
          error: sanitizeError(err),
        }
      );
    }
  }
}
